import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { staggerContainerVariants, fadeUpVariants } from './useScrollReveal';

const FORMSPREE_URL = 'https://formspree.io/f/xeeppjjq';

const Contact: React.FC = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', business: '', service: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [focused, setFocused] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('Please fill in your name, email, and project details.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(FORMSPREE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) setSubmitted(true);
      else {
        const data = await res.json();
        setError(data?.errors?.[0]?.message || 'Something went wrong. Please email us at Create@SiteStackCo.com');
      }
    } catch {
      setError('Something went wrong. Please email us directly at Create@SiteStackCo.com');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (name: string) =>
    `w-full bg-transparent border rounded-xl px-4 py-3.5 text-white font-dm text-sm placeholder-[#666] focus:outline-none transition-all duration-300 ${
      focused === name ? 'border-lime/40' : 'border-[#1a1a1a]'
    }`;

  return (
    <section id="contact" className="py-32 bg-[#080808] border-t border-[#0f0f0f]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">
          {/* Left */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainerVariants}
          >
            <motion.p variants={fadeUpVariants} className="section-label mb-6">Get In Touch</motion.p>
            <motion.h2
              variants={fadeUpVariants}
              className="font-serif text-[clamp(2.2rem,5vw,4.5rem)] text-white leading-tight mb-8"
            >
              Let's Build Something{' '}
              <span className="italic text-lime">Great.</span>
            </motion.h2>
            <motion.p variants={fadeUpVariants} className="font-dm text-[#aaa] text-base leading-relaxed mb-12 max-w-sm">
              Ready to take your business to the next level? Fill out the form and we'll be in touch within 24 hours.
            </motion.p>

            <motion.div variants={fadeUpVariants} className="space-y-5 mb-10">
              {[
                { icon: '✉', label: 'Email', value: 'Create@SiteStackCo.com' },
                { icon: '📍', label: 'Based In', value: 'Winchester, VA — Nationwide' },
              ].map(item => (
                <div key={item.label} className="flex items-center gap-4">
                  <div className="w-px h-10 bg-lime flex-shrink-0" />
                  <div>
                    <p className="font-dm text-[#888] text-xs uppercase tracking-widest">{item.label}</p>
                    <p className="font-syne font-semibold text-white text-sm mt-0.5">{item.value}</p>
                  </div>
                </div>
              ))}
            </motion.div>

            <motion.div variants={fadeUpVariants} className="flex gap-3">
              {['Instagram', 'Facebook', 'LinkedIn', 'TikTok'].map(s => (
                <a
                  key={s}
                  href="https://sitestackco.com"
                  target="_blank"
                  rel="noreferrer"
                  className="w-9 h-9 rounded-full border border-[#1a1a1a] flex items-center justify-center font-syne font-bold text-[#888] hover:border-lime hover:text-lime transition-all text-xs"
                >
                  {s[0]}
                </a>
              ))}
            </motion.div>

            {/* Big decorative text */}
            <motion.div
              variants={fadeUpVariants}
              className="mt-16 hidden lg:block"
            >
              <p className="font-serif text-[7rem] text-[#0f0f0f] leading-none select-none pointer-events-none">✦</p>
            </motion.div>
          </motion.div>

          {/* Right — Form */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22,1,0.36,1] }}
            className="border border-[#141414] rounded-2xl p-8 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-40 h-40 pointer-events-none"
              style={{ background: 'radial-gradient(circle at top right, rgba(200,241,53,0.04), transparent 70%)' }} />

            <AnimatePresence mode="wait">
              {submitted ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center justify-center h-full text-center py-16"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
                    className="w-16 h-16 rounded-full bg-lime flex items-center justify-center mb-6"
                  >
                    <svg className="w-7 h-7 text-[#080808]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </motion.div>
                  <h3 className="font-serif text-2xl text-white mb-3">Message Sent!</h3>
                  <p className="font-dm text-[#999] text-sm mb-8 max-w-xs">We'll be in touch within 24 hours to schedule your free strategy call.</p>
                  <button
                    onClick={() => { setSubmitted(false); setForm({ name:'',email:'',phone:'',business:'',service:'',message:'' }); }}
                    className="font-dm text-[#888] hover:text-lime text-xs transition-colors"
                  >
                    Send another message
                  </button>
                </motion.div>
              ) : (
                <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <h3 className="font-syne font-bold text-white text-lg mb-7">Send a Message</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <input type="text" name="name" value={form.name} onChange={handleChange}
                        placeholder="Full Name *" className={inputClass('name')}
                        onFocus={() => setFocused('name')} onBlur={() => setFocused('')} />
                      <input type="email" name="email" value={form.email} onChange={handleChange}
                        placeholder="Email Address *" className={inputClass('email')}
                        onFocus={() => setFocused('email')} onBlur={() => setFocused('')} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <input type="tel" name="phone" value={form.phone} onChange={handleChange}
                        placeholder="Phone Number" className={inputClass('phone')}
                        onFocus={() => setFocused('phone')} onBlur={() => setFocused('')} />
                      <input type="text" name="business" value={form.business} onChange={handleChange}
                        placeholder="Business Name" className={inputClass('business')}
                        onFocus={() => setFocused('business')} onBlur={() => setFocused('')} />
                    </div>
                    <select name="service" value={form.service} onChange={handleChange}
                      className={inputClass('service')}
                      onFocus={() => setFocused('service')} onBlur={() => setFocused('')}
                      style={{ color: form.service ? '#fff' : '#666' }}
                    >
                      <option value="">Service Interested In...</option>
                      <option>Web Design & Development</option>
                      <option>Branding & Identity</option>
                      <option>Photography & Videography</option>
                      <option>Digital Marketing</option>
                      <option>Full Suite Package</option>
                      <option>Not sure — let's talk</option>
                    </select>
                    <textarea name="message" value={form.message} onChange={handleChange} rows={4}
                      placeholder="Tell us about your project... *"
                      className={`${inputClass('message')} resize-none`}
                      onFocus={() => setFocused('message')} onBlur={() => setFocused('')} />

                    <AnimatePresence>
                      {error && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0 }}
                          className="font-dm text-red-400 text-xs bg-red-400/10 border border-red-400/20 rounded-xl px-4 py-3"
                        >
                          {error}
                        </motion.p>
                      )}
                    </AnimatePresence>

                    <motion.button
                      onClick={handleSubmit}
                      disabled={loading}
                      className="btn-lime w-full py-4 text-sm gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                    >
                      {loading ? (
                        <>
                          <svg className="w-4 h-4 animate-spin inline mr-2" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                          </svg>
                          Sending...
                        </>
                      ) : 'Send Message & Book Free Call'}
                    </motion.button>
                    <p className="font-dm text-[#777] text-xs text-center">We respond within 24 hours. No spam, ever.</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
